import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";

import * as XLSX from "xlsx";

// Icons
import Search from "@material-ui/icons/Search";
import TrendingUp from "@material-ui/icons/TrendingUp";
import ShoppingCart from "@material-ui/icons/ShoppingCart";
import CheckCircle from "@material-ui/icons/CheckCircle";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import MoreVert from "@material-ui/icons/MoreVert";
import Person from "@material-ui/icons/Person";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

// core components
import DoobiHeader from "components/Headers/DoobiHeader";
import BranchBar from "./BranchBar";
import config from "../../../config";
import { BUSINESS_INFO, pad2, formatReadyDate, writeReceiptToWindow } from "./receipt";
import {
    buildDisplayItems,
    getServiceTypesForItem,
    getServiceTypeName,
    getItemCategoryLabel,
    getExpressPrice,
    toOrderLineRef
} from "./posCatalog";

// Icon for the reprint action
import Print from "@material-ui/icons/Print";

const formatBd = (value) => `${(Number(value) || 0).toFixed(3)} BD`;

// Orders store the discount as an amount, but it is entered as a percentage -- recover the rate
// for the edit field. Rounded to 3dp and trimmed so a 10% discount seeds as "10", not
// "9.999999999", while an awkward stored amount still round-trips close enough that reopening an
// order and saving it unchanged does not move the figure.
const toPercentInput = (amount, subtotal) => {
    if (!subtotal) return "0";
    const percent = Math.min(100, Math.max(0, (amount / subtotal) * 100));
    return String(Number(percent.toFixed(3)));
};

// The From/To inputs give plain local calendar dates, so an order's timestamp has to be
// reduced to a local date key to be comparable. toISOString() would key it by the UTC date,
// which in Bahrain (UTC+3) pulls 00:00–03:00 of the next day back into the previous day.
const toLocalDateKey = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const TENDER_COLORS = {
    Cash: "#2dce89",
    Card: "#5e72e4",
    Benefit: "#11cdef",
    "Pay on Collection": "#fb6340"
};

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        // 1.25rem to match the stats grid and the tender strip, so all three
        // rows on this page are separated by the same gap
        marginBottom: "1.25rem",
        borderRadius: "15px",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    },
    // Icon sits beside the figure rather than above it, so the row stays short and the
    // order table keeps its place on the first screen.
    statCard: {
        borderRadius: "15px",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        // 1rem vertical brings these to roughly the height of the tender strip
        // below, which runs ~80px: two text rows (~48px) plus the cell's 0.5rem
        // and the strip's 1.5rem of padding.
        padding: "1rem",
        transition: "transform 0.2s",
        // The global MuiCard rule adds 30px below every card, which stacked on
        // top of the Grid container's own 1.25rem and made the gap under this
        // row 50px against 20px everywhere else. `&&` for specificity -- see
        // the same fix on itemCard in Pos.js.
        "&&": {
            marginBottom: 0
        },
        "&:hover": {
            transform: "translateY(-4px)"
        }
    },
    iconShape: {
        width: "36px",
        height: "36px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        marginRight: "0.875rem",
        backgroundColor: theme.palette.primary.main,
        color: "#fff"
    },
    // One strip for all tenders instead of a card each — they read as a single
    // settlement figure split four ways, and it costs one row instead of two.
    tenderStrip: {
        borderRadius: "15px",
        padding: "0.75rem 0.5rem",
        marginBottom: "1.25rem",
        display: "flex",
        // The theme defaults every MuiCard to flexDirection: column; without this the
        // cells stack and their flex-basis is read as a height.
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .1)"
    },
    tenderCell: {
        flex: "1 1 160px",
        padding: "0.25rem 1rem",
        borderRight: "1px solid #edf0f7",
        "&:last-child": {
            borderRight: "none"
        }
    },
    tenderSwatch: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        marginRight: "8px",
        flexShrink: 0
    },
    filterBox: {
        padding: "1rem 1.25rem",
        backgroundColor: "#fff",
        borderRadius: "15px",
        marginBottom: "1.25rem",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .1)",
        // Whatever lands first here straddles the header's colour band by design — the analytics
        // cards for admins, this row when they are hidden. The header is position: relative, so a
        // static sibling would be painted over and clipped; stack above it instead.
        position: "relative",
        zIndex: 1,
    },
    tableContainer: {
        borderRadius: "15px",
        overflow: "hidden"
    },
    statusChip: {
        fontWeight: "bold",
        borderRadius: "6px"
    },
    priceCell: {
        fontWeight: "bold",
        color: theme.palette.primary.main
    },
    orderItemRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "12px 14px",
        border: "1px solid #e9ecef",
        borderRadius: "10px",
        marginBottom: "10px",
        backgroundColor: "#fafbff"
    },
    orderItemMeta: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "8px"
    },
    orderItemBadge: {
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: "#eef2ff",
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "0.75rem"
    },
    orderItemPriceNote: {
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: "#f1f3f5",
        color: "#525f7f",
        fontWeight: 600,
        fontSize: "0.75rem"
    },
    addItemBox: {
        padding: "12px 14px",
        border: "1px dashed #cbd3e1",
        borderRadius: "10px",
        marginTop: "4px"
    }
}));

function PosSales() {
    const classes = useStyles();
    const theme = useTheme();
    const isAdmin = (localStorage.getItem("userRoles") || "").toUpperCase().includes("ADMIN");

    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("all");
    const [laundries, setLaundries] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'placedOn', direction: 'desc' });

    // Customer Dialog State
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Anchor for Status Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeOrderId, setActiveOrderId] = useState(null);

    // When completing a "Pay on Collection" order, ask the admin for the actual payment type.
    const [collectPayOrder, setCollectPayOrder] = useState(null);
    const COLLECTION_PAYMENT_METHODS = ["Cash", "Card", "Benefit"];

    // Admin edit of a placed order: adjust quantities, drop lines, change how it was paid.
    const [editOrder, setEditOrder] = useState(null);
    const [editItems, setEditItems] = useState([]);
    const [editPaymentMethod, setEditPaymentMethod] = useState("");
    // A percentage of the subtotal, matching the basket. The order stores the amount.
    const [editDiscount, setEditDiscount] = useState("0");
    const [editSaving, setEditSaving] = useState(false);
    const EDIT_PAYMENT_METHODS = ["Cash", "Card", "Benefit", "Pay on Collection"];

    // Service catalogue, loaded on first use, backing the "add item" picker in the editor.
    const [catalogItems, setCatalogItems] = useState([]);
    const [globalServiceTypes, setGlobalServiceTypes] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [addServiceId, setAddServiceId] = useState("");
    const [addServiceTypeId, setAddServiceTypeId] = useState("");

    useEffect(() => {
        fetchOrders();
        fetchLaundries();
    }, []);

    const fetchLaundries = async () => {
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const response = await fetch(`${config.WS_BASE_URL}app/laundry/active`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            const data = await response.json();
            if (response.ok) {
                setLaundries(data.data || (Array.isArray(data) ? data : []));
            }
        } catch (error) {
            console.error("Error fetching laundries:", error);
        }
    };

    const getLaundryName = (laundryId) => {
        if (laundryId == null) return null;
        const match = laundries.find((l) => String(l.id) === String(laundryId));
        return match ? match.name : null;
    };

    // Per-branch order code (e.g. W00001). Falls back to the global id for orders
    // created before per-branch codes existed. Returned without a leading '#'.
    const displayOrderNo = (order) => {
        if (!order) return "—";
        if (order.branchOrderCode) return order.branchOrderCode;
        return order.id != null ? String(order.id) : "—";
    };

    const fetchOrders = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");
            const tenantId = localStorage.getItem("tenantId");
            const selectedCountry = localStorage.getItem("selectedCountry");

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            const data = await response.json();
            if (response.ok) {
                setOrders(Array.isArray(data) ? data : (data.data || []));
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateOrderStatus = async (orderId, newStatus, paymentMethod) => {
        try {
            const token = localStorage.getItem("token");
            const tenantId = localStorage.getItem("tenantId");
            const selectedCountry = localStorage.getItem("selectedCountry");

            let url = `${config.WS_BASE_URL}api/walk-in-order/${orderId}/status?status=${newStatus}`;
            if (paymentMethod) {
                url += `&paymentMethod=${encodeURIComponent(paymentMethod)}`;
            }

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });

            if (response.ok) {
                fetchOrders(); // Refresh table
                setDetailsOpen(false);
                setCollectPayOrder(null);
            } else if (response.status === 401 || response.status === 403) {
                alert("Orders must follow the washing → washed → completed flow. Only admins can change to any status or cancel.");
            } else {
                alert("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
        setAnchorEl(null);
    };

    // Completing a "Pay on Collection" order requires capturing how the customer actually paid,
    // so route those through the payment dialog; everything else completes directly.
    const handleCompleteOrder = (order) => {
        setAnchorEl(null);
        if (!order) return;
        if ((order.paymentMethod || "").toLowerCase() === "pay on collection") {
            setCollectPayOrder(order);
        } else {
            updateOrderStatus(order.id, 3);
        }
    };

    // Loads the full order (list rows don't always carry service names) and seeds the edit form.
    const handleOpenEdit = async (order) => {
        setAnchorEl(null);
        if (!order) return;
        let full = order;
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${order.id}`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            if (response.ok) {
                full = await response.json();
            }
        } catch (error) {
            console.error("Error loading order for edit:", error);
        }
        setEditOrder(full);
        // `key` identifies a row in this dialog for the whole of its life; `id` is the
        // persisted row and stays null for lines added here, which is what tells the backend
        // to create and price them.
        setEditItems((full.walkInOrderItemsById || []).map((it) => ({
            key: `stored-${it.id}`,
            id: it.id,
            serviceId: it.serviceId,
            serviceTypeId: it.serviceTypeId ?? null,
            name: it.serviceByServiceId?.nameEn || `Service ${it.serviceId || ""}`.trim(),
            typeName: it.serviceType?.nameEn || "",
            price: it.price,
            quantity: it.quantity
        })));
        setEditPaymentMethod(full.paymentMethod || "");
        // Discounts are entered as a percentage here, the same as in the basket. The order stores
        // the resulting amount, so recover the rate it came from to seed the field.
        setEditDiscount(toPercentInput(Number(full.discount) || 0, Number(full.subtotal) || 0));
        loadCatalog();
    };

    // The service catalogue is only needed once an admin actually opens the editor, so it is
    // fetched on first use rather than with the orders table. Same endpoints the till uses.
    const loadCatalog = async () => {
        if (catalogItems.length > 0 || catalogLoading) return;
        try {
            setCatalogLoading(true);
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const headers = {
                'Authorization': token || '',
                'Country': selectedCountry || '',
                'Content-Type': 'application/json',
                'X-Tenant-Key': tenantId || 'main'
            };

            const [servicesRes, typesRes] = await Promise.all([
                fetch(`${config.WS_BASE_URL}app/service/activeForPos`, { headers }),
                fetch(`${config.WS_BASE_URL}app/service-type`, { headers })
            ]);

            if (servicesRes.ok) {
                const payload = await servicesRes.json();
                const data = payload?.data || (Array.isArray(payload) ? payload : []);
                setCatalogItems([...data].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0)));
            }
            if (typesRes.ok) {
                const payload = await typesRes.json();
                setGlobalServiceTypes(Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []));
            }
        } catch (error) {
            console.error("Error loading service catalogue:", error);
        } finally {
            setCatalogLoading(false);
        }
    };

    const closeEdit = () => {
        setEditOrder(null);
        setEditItems([]);
        setEditPaymentMethod("");
        setEditDiscount("0");
        setAddServiceId("");
        setAddServiceTypeId("");
    };

    const changeEditQuantity = (key, delta) => {
        setEditItems((items) => items.map((it) => (
            it.key === key ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it
        )));
    };

    const removeEditItem = (key) => {
        setEditItems((items) => items.filter((it) => it.key !== key));
    };

    // The sellable items and the service types available for the one currently picked.
    const addableItems = buildDisplayItems(catalogItems);
    const addSelectedItem = addableItems.find((it) => String(it.id) === String(addServiceId)) || null;
    const addServiceTypes = addSelectedItem ? getServiceTypesForItem(addSelectedItem) : [];

    // Adds a line priced the way the till would price it for THIS order — the order's own
    // express flag and percentage, not the current defaults, since the stored unit prices are
    // express-inclusive. The backend re-derives this independently; it stays the authority.
    const addEditItem = () => {
        if (!addSelectedItem || !editOrder) return;
        const serviceType = addServiceTypes.find((t) => String(t.serviceTypeId ?? t.id) === String(addServiceTypeId));
        if (!serviceType) return;

        const ref = toOrderLineRef(addSelectedItem, serviceType);
        const price = getExpressPrice(serviceType.price, !!editOrder.express, editOrder.expressPercentage);

        setEditItems((items) => {
            // Adding something already on the order bumps its quantity, matching the till.
            const existing = items.find((it) => (
                String(it.serviceId) === String(ref.serviceId)
                && String(it.serviceTypeId ?? "") === String(ref.serviceTypeId ?? "")
            ));
            if (existing) {
                return items.map((it) => (
                    it.key === existing.key ? { ...it, quantity: it.quantity + 1 } : it
                ));
            }
            return [...items, {
                key: `new-${ref.serviceId}-${ref.serviceTypeId ?? "none"}-${Date.now()}`,
                id: null,
                serviceId: ref.serviceId,
                serviceTypeId: ref.serviceTypeId ?? null,
                name: addSelectedItem.nameEn,
                typeName: getServiceTypeName(globalServiceTypes, serviceType),
                price,
                quantity: 1
            }];
        });
        setAddServiceId("");
        setAddServiceTypeId("");
    };

    // Preview of what the server will store. VAT was stored as an amount rather than a rate, so
    // the original rate is recovered and re-applied — the same arithmetic editOrder() does on the
    // backend, which stays the authority on the saved figures. The discount is whatever the
    // admin has typed; `discountError` is non-null when that value can't be saved.
    const editTotals = (() => {
        const subtotal = editItems.reduce((acc, it) => acc + (Number(it.price) || 0) * it.quantity, 0);
        if (!editOrder) return { subtotal, discount: 0, deliveryCharge: 0, vat: 0, total: subtotal, discountError: null };

        const deliveryCharge = Number(editOrder.deliveryCharge) || 0;
        const originalTaxable = editOrder.subtotal - editOrder.discount + deliveryCharge;
        const vatRate = originalTaxable > 0 ? editOrder.vat / originalTaxable : 0;

        // Entered as a percentage of the subtotal, as in the basket; the API stores the amount.
        const parsedDiscount = parseFloat(editDiscount);
        let discountError = null;
        if (!Number.isFinite(parsedDiscount)) {
            discountError = "Enter a discount percentage.";
        } else if (parsedDiscount < 0) {
            discountError = "Discount cannot be negative.";
        } else if (parsedDiscount > 100) {
            discountError = "Discount cannot exceed 100%.";
        }

        const discount = discountError ? 0 : subtotal * parsedDiscount / 100;
        const taxable = Math.max(0, subtotal - discount + deliveryCharge);
        const vat = taxable * vatRate;
        return { subtotal, discount, deliveryCharge, vat, total: taxable + vat, discountError };
    })();

    const saveEdit = async () => {
        if (!editOrder || editItems.length === 0 || editTotals.discountError) return;
        try {
            setEditSaving(true);
            const token = localStorage.getItem("token");
            const tenantId = localStorage.getItem("tenantId");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${editOrder.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                },
                body: JSON.stringify({
                    paymentMethod: editPaymentMethod || null,
                    discount: editTotals.discount,
                    // Existing lines go back by id; new ones carry the service they were
                    // picked from so the server can price them itself.
                    items: editItems.map((it) => (it.id
                        ? { id: it.id, quantity: it.quantity }
                        : { serviceId: it.serviceId, serviceTypeId: it.serviceTypeId, quantity: it.quantity }
                    ))
                })
            });
            if (response.ok) {
                closeEdit();
                setDetailsOpen(false);
                fetchOrders();
            } else {
                const data = await response.json().catch(() => ({}));
                alert(data.message || "Failed to save changes.");
            }
        } catch (error) {
            console.error("Error saving order edit:", error);
            alert("Failed to save changes.");
        } finally {
            setEditSaving(false);
        }
    };

    const handleViewDetails = async (order) => {
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${order.id}`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            const data = await response.json();
            if (response.ok) {
                setSelectedOrder(data);
                setDetailsOpen(true);
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            // Fallback to local data if needed
            setSelectedOrder(order);
            setDetailsOpen(true);
        }
    };

    const getCustomer = (order) => {
        return order.walkInCustomer || order.walkInCustomerByWalkInCustomerId;
    };

    const getLaundry = (laundryId) => {
        if (laundryId == null) return null;
        return laundries.find((l) => String(l.id) === String(laundryId)) || null;
    };

    // Shapes a stored walk-in order into the data object the shared receipt renderer expects.
    // Branch header comes from the order's own laundry so an admin reprinting another branch's
    // ticket gets that branch's details rather than the logged-in user's branch.
    const buildReceiptDataFromOrder = (order) => {
        const customer = getCustomer(order);
        const laundry = getLaundry(order.laundryId);
        const placed = order.placedOn ? new Date(order.placedOn) : null;
        const dateStr = placed && !isNaN(placed.getTime())
            ? `${pad2(placed.getDate())}/${pad2(placed.getMonth() + 1)}/${String(placed.getFullYear()).slice(-2)} ${pad2(placed.getHours())}:${pad2(placed.getMinutes())}`
            : "";
        const items = (order.walkInOrderItemsById || []).map((it) => ({
            name: it.serviceByServiceId?.nameEn || `Service ${it.serviceId || it.service_id || ""}`.trim(),
            serviceName: it.serviceType?.nameEn || "",
            express: !!order.express,
            price: it.price,
            qty: it.quantity,
            total: it.total != null ? it.total : (it.price || 0) * (it.quantity || 0),
        }));
        return {
            orderNo: displayOrderNo(order),
            pieces: items.reduce((acc, it) => acc + (it.qty || 0), 0),
            business: {
                name: laundry?.name || localStorage.getItem("laundryName") || BUSINESS_INFO.name,
                address: laundry?.address || localStorage.getItem("laundryAddress") || BUSINESS_INFO.address,
                tel: laundry?.phoneNumber || localStorage.getItem("laundryPhone") || BUSINESS_INFO.tel,
            },
            servedBy: order.createdBy || "",
            customerName: customer ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() : "",
            customerRef: customer ? (customer.mobileNumber || customer.phoneNumber || "") : "",
            items,
            subtotal: order.subtotal || 0,
            discount: order.discount || 0,
            deliveryCharge: order.deliveryCharge || 0,
            vat: order.vat || 0,
            total: order.total || 0,
            paymentMethod: order.paymentMethod,
            express: !!order.express,
            expressPercentage: order.expressPercentage || 0,
            date: dateStr,
            readyDate: formatReadyDate(order.readyDate),
        };
    };

    // Reprints the receipt for an existing order. The print window is opened on the click
    // gesture (before any await) so pop-up blockers don't kill it; we then pull the full
    // order so item/service names are populated, falling back to the row data on error.
    const reprintReceipt = async (order) => {
        setAnchorEl(null);
        if (!order) return;
        const win = window.open("", "PRINT", "height=600,width=400");
        let full = order;
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${order.id}`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            if (response.ok) {
                full = await response.json();
            }
        } catch (error) {
            console.error("Error loading order for reprint:", error);
        }
        writeReceiptToWindow(win, buildReceiptDataFromOrder(full), "Reprint");
    };

    // True when a timestamp falls inside the From/To range the user picked (an empty range
    // matches everything). Used against the order date for the list below and against the
    // payment date for the settlement panel — the two are not the same day for an order
    // taken as "Pay on Collection".
    const inSelectedRange = (value) => {
        if (!startDate && !endDate) return true;
        const key = toLocalDateKey(value);
        if (!key) return false;
        if (startDate && key < startDate) return false;
        if (endDate && key > endDate) return false;
        return true;
    };

    const filteredOrders = orders.filter(order => {
        const customer = getCustomer(order);
        const matchesSearch = order.id.toString().includes(searchTerm) ||
            (order.branchOrderCode && order.branchOrderCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (customer?.firstName && customer.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (customer?.mobileNumber && customer.mobileNumber.includes(searchTerm));
        const matchesStatus = statusFilter === "all" || order.status.toString() === statusFilter;
        const matchesBranch = branchFilter === "all" || String(order.laundryId) === String(branchFilter);

        return matchesSearch && matchesStatus && inSelectedRange(order.placedOn) && matchesBranch;
    }).sort((a, b) => {
        if (sortConfig.key) {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Handle nested properties or specific keys
            if (sortConfig.key === 'customer') {
                const customerA = getCustomer(a);
                const customerB = getCustomer(b);
                aValue = customerA?.firstName || '';
                bValue = customerB?.firstName || '';
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
        }
        return 0;
    });

    const analytics = {
        totalSales: filteredOrders.reduce((acc, o) => acc + (o.total || 0), 0),
        totalOrders: filteredOrders.length,
        completedOrders: filteredOrders.filter(o => o.status === 3).length,
        pendingOrders: filteredOrders.filter(o => [0, 1, 2].includes(o.status)).length,
    };

    // Orders whose money was actually taken inside the selected range, keyed on the payment date
    // rather than the order date. An order placed on the 1st and collected on the 9th belongs to
    // the 9th's till, which is the whole point of tracking a payment date separately.
    //
    // Deliberately not narrowed by the search box or the status dropdown: this is a till figure
    // that has to reconcile against what the branch banked, and a partial total that silently
    // omits orders is worse than no total. Branch still applies — each branch banks separately.
    // Cancelled orders drop out; anything refunded was not kept.
    const settledOrders = orders.filter((order) =>
        order.paidOn
        && order.status !== 4
        && (branchFilter === "all" || String(order.laundryId) === String(branchFilter))
        && inSelectedRange(order.paidOn)
    );

    // Orders still owed for. These have no payment date by definition, so they cannot be ranged
    // on one — they are outstanding as of now regardless of the dates picked.
    const unsettledOrders = orders.filter((order) =>
        !order.paidOn
        && order.status !== 4
        && (branchFilter === "all" || String(order.laundryId) === String(branchFilter))
    );

    // Settlement view for the selected range: what was taken through each tender.
    // "Pay on Collection" is only shown when some orders are still unsettled, since those
    // have not landed in a till or on the card machine yet.
    const paymentBreakdown = (() => {
        const buckets = new Map(
            ["Cash", "Card", "Benefit"].map((label) => [label, { label, total: 0, count: 0 }])
        );
        settledOrders.forEach((order) => {
            const label = (order.paymentMethod || "").trim();
            const key = [...buckets.keys()].find((k) => k.toLowerCase() === label.toLowerCase());
            if (!key) return;
            const bucket = buckets.get(key);
            bucket.total += order.total || 0;
            bucket.count += 1;
        });
        const rows = [...buckets.values()];
        if (unsettledOrders.length > 0) {
            rows.push({
                label: "Pay on Collection",
                total: unsettledOrders.reduce((acc, o) => acc + (o.total || 0), 0),
                count: unsettledOrders.length,
                outstanding: true,
            });
        }
        return rows;
    })();

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 0: return { label: "Placed", color: "#fb6340", bgColor: "#fff1ee" };
            case 1: return { label: "Washing", color: "#11cdef", bgColor: "#e0f7fa" };
            case 2: return { label: "Washed", color: "#8965e0", bgColor: "#f3f0fb" };
            case 3: return { label: "Completed", color: "#2dce89", bgColor: "#e7fcf1" };
            case 4: return { label: "Cancelled", color: "#f5365c", bgColor: "#fee7eb" };
            default: return { label: "Unknown", color: "#adb5bd", bgColor: "#f8f9fe" };
        }
    };

    // Builds a flat, fully-populated row for each order currently matched by the on-screen
    // filters and exports them as a .xlsx workbook. Money and counts stay numeric so Excel can
    // sum them; line items (when present in the list payload) are flattened into one cell.
    const handleDownloadExcel = () => {
        // Everything matched on screen, plus anything settled inside the range that the on-screen
        // filters left out because it was ordered earlier. Without that second group the workbook
        // could not reconcile a day's cash by payment date at all — the late-collected orders,
        // the exact ones that made the old reports wrong, would be missing from it.
        const exported = [...filteredOrders];
        const included = new Set(filteredOrders.map((o) => o.id));
        settledOrders.forEach((order) => {
            if (!included.has(order.id)) exported.push(order);
        });

        const rows = exported.map((order) => {
            const customer = getCustomer(order);
            const placed = order.placedOn ? new Date(order.placedOn) : null;
            const paid = order.paidOn ? new Date(order.paidOn) : null;
            const items = (order.walkInOrderItemsById || [])
                .map((it) => {
                    const name = it.serviceByServiceId?.nameEn || `Service ${it.serviceId || it.service_id || ""}`.trim();
                    const type = it.serviceType?.nameEn ? ` (${it.serviceType.nameEn})` : "";
                    return `${name}${type} x${it.quantity || 0}`;
                })
                .join("; ");
            return {
                "Order ID": displayOrderNo(order),
                "Global ID": order.id,
                "Date": placed && !isNaN(placed.getTime()) ? placed.toLocaleDateString() : "",
                "Time": placed && !isNaN(placed.getTime()) ? placed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
                "Customer Name": customer ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() : "",
                "Mobile": customer?.mobileNumber || "",
                "Email": customer?.emailAddress || "",
                "Branch": getLaundryName(order.laundryId) || "",
                "Served By": order.createdBy || "",
                "Status": getStatusInfo(order.status).label,
                "Payment Method": order.paymentMethod || "",
                // The date the money was actually taken, which is what a daily cash/card
                // reconciliation has to be summed on — not the order Date column above.
                // Blank means still unpaid, or an order settled before payment dates were
                // recorded (those were never captured and cannot be recovered).
                "Payment Date": paid && !isNaN(paid.getTime()) ? paid.toLocaleDateString() : "",
                "Payment Time": paid && !isNaN(paid.getTime()) ? paid.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
                "Express": order.express ? "Yes" : "No",
                "Express %": order.expressPercentage || 0,
                "Subtotal (BD)": Number(order.subtotal) || 0,
                "Discount (BD)": Number(order.discount) || 0,
                "Delivery Charge (BD)": Number(order.deliveryCharge) || 0,
                "VAT (BD)": Number(order.vat) || 0,
                "Total (BD)": Number(order.total) || 0,
                "Items": items,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "POS Sales");
        const stamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `pos-sales-${stamp}.xlsx`);
    };

    return (
        <Box style={{ backgroundColor: "#f8f9fe", minHeight: "100vh" }}>
            <DoobiHeader section="POS Sales" subsection="Manage your POS sales history" />
            {/* -4.5rem to match DoobiHeader's paddingBottom -- see the note in Pos.js */}
            <Container maxWidth={false} component={Box} marginTop="-4.5rem">
                <BranchBar dense />
                {/* Analytics Segment — admin only: till operators see their own orders below,
                    but not the branch's revenue and order totals.

                    On the grid's marginBottom: spacing={2} puts 8px of padding on
                    each Grid item, and an explicit marginBottom replaces the
                    container's compensating -8px, so the visible gap is 8px plus
                    the value below. 0.5rem lands it at 12px -- tighter than the
                    20px between the rows further down. */}
                {isAdmin && (
                <Grid container spacing={2} style={{ marginBottom: '0.5rem' }}>
                    {[
                        { label: "TOTAL REVENUE", value: formatBd(analytics.totalSales), icon: <TrendingUp />, color: theme.palette.primary.main },
                        { label: "TOTAL ORDERS", value: analytics.totalOrders, icon: <ShoppingCart />, color: theme.palette.info.main },
                        { label: "COMPLETED", value: analytics.completedOrders, icon: <CheckCircle />, color: "#2dce89" },
                        { label: "PENDING", value: analytics.pendingOrders, icon: <HourglassEmpty />, color: "#fb6340" },
                    ].map((stat) => (
                        <Grid item xs={12} sm={6} md={3} key={stat.label}>
                            <Card className={classes.statCard}>
                                <Box className={classes.iconShape} style={{ backgroundColor: stat.color }}>
                                    {stat.icon}
                                </Box>
                                <Box minWidth={0}>
                                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>{stat.label}</Typography>
                                    <Typography variant="h4" style={{ fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                )}

                {/* Payment breakdown for the selected range — used to reconcile the till and
                    the card machine against the day's takings. Admin only, for the same reason
                    as the analytics grid above: it is branch revenue.

                    Note these figures range on the PAYMENT date while the order table below
                    ranges on the order date, so the two are not expected to tie out: an order
                    collected today but taken last week counts here and not there. That is the
                    correct behaviour for reconciliation — the till only saw it today. */}
                {isAdmin && (
                <Card className={classes.tenderStrip}>
                    {paymentBreakdown.map((tender) => (
                        <Box className={classes.tenderCell} key={tender.label}>
                            <Box display="flex" alignItems="center">
                                <Box
                                    className={classes.tenderSwatch}
                                    style={{ backgroundColor: TENDER_COLORS[tender.label] || "#adb5bd" }}
                                />
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                    {tender.label.toUpperCase()}
                                </Typography>
                                {/* This one is money not yet taken, so it is everything still
                                    owed rather than a figure for the selected dates. Say so,
                                    or it reads as part of the same range as its neighbours. */}
                                {tender.outstanding && (
                                    <Typography variant="caption" style={{ marginLeft: 6, color: "#8898aa" }}>
                                        (outstanding)
                                    </Typography>
                                )}
                            </Box>
                            <Box display="flex" alignItems="baseline" style={{ gap: "8px" }}>
                                <Typography variant="h5" style={{ fontWeight: 700 }}>{formatBd(tender.total)}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {tender.count} {tender.count === 1 ? "order" : "orders"}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Card>
                )}

                {/* Filters Row */}
                <Box className={classes.filterBox}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md>
                            <TextField
                                fullWidth
                                placeholder="Search by Order ID or Customer..."
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search color="disabled" /></InputAdornment>
                                }}
                            />
                        </Grid>
                        {isAdmin && (
                        <Grid item xs={12} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Branch"
                                variant="outlined"
                                size="small"
                                value={branchFilter}
                                onChange={(e) => setBranchFilter(e.target.value)}
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="all">All Branches</option>
                                {laundries.map((laundry) => (
                                    <option key={laundry.id} value={laundry.id}>
                                        {laundry.name}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        )}
                        <Grid item xs={12} md={2}>
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="0">Placed</option>
                                <option value="1">Washing</option>
                                <option value="2">Washed</option>
                                <option value="3">Completed</option>
                                <option value="4">Cancelled</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="From"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="To"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md="auto" style={{ marginLeft: "auto" }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isLoading || filteredOrders.length === 0}
                                onClick={handleDownloadExcel}
                                style={{ height: "40px", padding: "0 12px", minWidth: "unset", textTransform: "none", fontWeight: 600, boxShadow: "none", whiteSpace: "nowrap" }}
                            >
                                Download
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {/* Orders Table */}
                <Card className={classes.cardRoot}>
                    {/* Plain div, not a Paper: MuiPaper.root carries the cream
                        yellow10 tint, which showed through as a yellow wash
                        behind the table. This already sits inside a white Card
                        and had elevation={0}, so Paper added nothing else. */}
                    <TableContainer>
                        <Table>
                            <TableHead style={{ backgroundColor: "#f6f9fc" }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'id'}
                                            direction={sortConfig.key === 'id' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('id')}
                                        >
                                            ORDER ID
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'placedOn'}
                                            direction={sortConfig.key === 'placedOn' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('placedOn')}
                                        >
                                            DATE
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'customer'}
                                            direction={sortConfig.key === 'customer' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('customer')}
                                        >
                                            CUSTOMER
                                        </TableSortLabel>
                                    </TableCell>
                                    {isAdmin && (
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>BRANCH / SERVED BY</TableCell>
                                    )}
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'total'}
                                            direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('total')}
                                        >
                                            TOTAL
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'status'}
                                            direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('status')}
                                        >
                                            STATUS
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 7 : 6} align="center" style={{ padding: '60px' }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 7 : 6} align="center" style={{ padding: '60px' }}>
                                            <Typography variant="body1" color="textSecondary">No orders found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders.map((order) => {
                                    const statusInfo = getStatusInfo(order.status);
                                    return (
                                        <TableRow key={order.id} hover>
                                            <TableCell style={{ fontWeight: 600 }}>{displayOrderNo(order)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {new Date(order.placedOn).toLocaleDateString()}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(order.placedOn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const customer = getCustomer(order);
                                                    return customer ? (
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="body2" style={{ fontWeight: 600, marginRight: '8px' }}>
                                                                {customer.mobileNumber}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedCustomer(customer);
                                                                    setCustomerDialogOpen(true);
                                                                }}
                                                            >
                                                                <Person fontSize="small" color="primary" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : `ID: ${order.walkInCustomerId}`;
                                                })()}
                                            </TableCell>
                                            {isAdmin && (
                                            <TableCell>
                                                <Typography variant="body2" color={getLaundryName(order.laundryId) ? "textPrimary" : "textSecondary"}>
                                                    {getLaundryName(order.laundryId) || "—"}
                                                </Typography>
                                                {order.createdBy ? (
                                                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                        by {order.createdBy}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            )}
                                            <TableCell className={classes.priceCell}>
                                                {formatBd(order.total)}
                                                {order.paymentMethod ? (
                                                    <Typography variant="caption" color="textSecondary" style={{ display: "block", fontWeight: 600 }}>
                                                        {order.paymentMethod}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={statusInfo.label}
                                                    className={classes.statusChip}
                                                    style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        style={{ textTransform: 'none', fontWeight: 600, marginRight: '8px' }}
                                                        onClick={() => handleViewDetails(order)}
                                                    >
                                                        View Items
                                                    </Button>

                                                    <Tooltip title="Reprint receipt">
                                                        <IconButton size="small" style={{ marginRight: '4px' }} onClick={() => reprintReceipt(order)}>
                                                            <Print fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {order.status === 0 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => updateOrderStatus(order.id, 1)}
                                                        >
                                                            Start Washing
                                                        </Button>
                                                    )}
                                                    {order.status === 1 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => updateOrderStatus(order.id, 2)}
                                                        >
                                                            Mark Washed
                                                        </Button>
                                                    )}
                                                    {order.status === 2 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => handleCompleteOrder(order)}
                                                        >
                                                            Complete
                                                        </Button>
                                                    )}

                                                    {isAdmin && (
                                                        <IconButton size="small" onClick={(e) => {
                                                            setAnchorEl(e.currentTarget);
                                                            setActiveOrderId(order.id);
                                                        }}>
                                                            <MoreVert fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Container>

            {/* Status Change Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <Tooltip title={!isAdmin ? "Only admin users can edit walk-in orders." : ""}>
                    <span style={{ display: "block" }}>
                        <MenuItem
                            disabled={!isAdmin || orders.find(o => o.id === activeOrderId)?.status === 4}
                            onClick={() => handleOpenEdit(orders.find(o => o.id === activeOrderId))}
                        >
                            Edit Order
                        </MenuItem>
                    </span>
                </Tooltip>
                <Divider />
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 0)}>Set to Placed</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 1)}>Set to Washing</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 2)}>Set to Washed</MenuItem>
                <MenuItem onClick={() => handleCompleteOrder(orders.find(o => o.id === activeOrderId))}>Set to Completed</MenuItem>
                <Tooltip title={!isAdmin ? "Only admin users can cancel walk-in orders." : ""}>
                    <span style={{ display: "block" }}>
                        <MenuItem disabled={!isAdmin} onClick={() => updateOrderStatus(activeOrderId, 4)}>
                            Set to Cancelled
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>

            {/* Edit Order Dialog (admin only): adjust quantities, drop lines, restate the payment method.
                Prices are not editable here — the server re-prices from the figures captured at checkout. */}
            <Dialog open={Boolean(editOrder)} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>Edit Order {displayOrderNo(editOrder)}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Totals are recalculated automatically. VAT keeps its original rate.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" gutterBottom color="primary">PAYMENT METHOD</Typography>
                    <TextField
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={editPaymentMethod}
                        onChange={(e) => setEditPaymentMethod(e.target.value)}
                        SelectProps={{ native: true }}
                        style={{ marginBottom: "1.5rem" }}
                    >
                        <option value="">Not specified</option>
                        {EDIT_PAYMENT_METHODS.map((method) => (
                            <option key={method} value={method}>{method}</option>
                        ))}
                    </TextField>

                    <Typography variant="subtitle2" gutterBottom color="primary">ORDER ITEMS</Typography>
                    {editItems.length === 0 ? (
                        <Typography variant="body2" color="error" style={{ marginBottom: "1rem" }}>
                            No items left. Add one below, or cancel the order instead.
                        </Typography>
                    ) : editItems.map((item) => (
                        <Box key={item.key} className={classes.orderItemRow}>
                            <Box>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>{item.name}</Typography>
                                {item.typeName && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                        {item.typeName}
                                    </Typography>
                                )}
                                <Box className={classes.orderItemMeta}>
                                    <Box className={classes.orderItemPriceNote}>{formatBd(item.price)} each</Box>
                                    {!item.id && <Box className={classes.orderItemBadge}>New</Box>}
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ gap: "4px" }}>
                                <IconButton size="small" onClick={() => changeEditQuantity(item.key, -1)} disabled={item.quantity <= 1}>
                                    <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" style={{ fontWeight: 700, minWidth: "24px", textAlign: "center" }}>
                                    {item.quantity}
                                </Typography>
                                <IconButton size="small" color="primary" onClick={() => changeEditQuantity(item.key, 1)}>
                                    <AddCircleOutline fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" style={{ fontWeight: 700, minWidth: "80px", textAlign: "right" }}>
                                    {formatBd((Number(item.price) || 0) * item.quantity)}
                                </Typography>
                                <Tooltip title="Remove item">
                                    <IconButton size="small" onClick={() => removeEditItem(item.key)}>
                                        <DeleteOutline fontSize="small" style={{ color: "#f5365c" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}

                    {/* Add an item. Prices shown here already include this order's express markup. */}
                    <Box className={classes.addItemBox}>
                        <Typography variant="subtitle2" gutterBottom color="primary">ADD ITEM</Typography>
                        {catalogLoading ? (
                            <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="textSecondary">Loading services...</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Item"
                                        variant="outlined"
                                        size="small"
                                        value={addServiceId}
                                        onChange={(e) => {
                                            setAddServiceId(e.target.value);
                                            setAddServiceTypeId("");
                                        }}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <option value="">Select an item</option>
                                        {addableItems.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {getItemCategoryLabel(item, catalogItems)} — {item.nameEn}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Service"
                                        variant="outlined"
                                        size="small"
                                        value={addServiceTypeId}
                                        onChange={(e) => setAddServiceTypeId(e.target.value)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={!addSelectedItem}
                                    >
                                        <option value="">Select a service</option>
                                        {addServiceTypes.map((type) => {
                                            const typeId = type.serviceTypeId ?? type.id;
                                            return (
                                                <option key={typeId} value={typeId}>
                                                    {getServiceTypeName(globalServiceTypes, type)} — {formatBd(getExpressPrice(type.price, !!editOrder?.express, editOrder?.expressPercentage))}
                                                </option>
                                            );
                                        })}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<AddCircleOutline />}
                                        disabled={!addSelectedItem || !addServiceTypeId}
                                        onClick={addEditItem}
                                        style={{ height: "40px", textTransform: "none", fontWeight: 600 }}
                                    >
                                        Add
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </Box>

                    <Divider style={{ margin: "1.25rem 0" }} />
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Subtotal</Typography>
                        <Typography variant="body2">{formatBd(editTotals.subtotal)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Discount</Typography>
                        <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                            {!editTotals.discountError && editTotals.discount > 0 && (
                                <Typography variant="body2" color="error">-{formatBd(editTotals.discount)}</Typography>
                            )}
                            <TextField
                                variant="outlined"
                                size="small"
                                type="number"
                                value={editDiscount}
                                onChange={(e) => setEditDiscount(e.target.value)}
                                error={Boolean(editTotals.discountError)}
                                inputProps={{ min: 0, max: 100, step: 0.5, style: { textAlign: "right" } }}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                style={{ width: "150px" }}
                            />
                        </Box>
                    </Box>
                    {editTotals.discountError && (
                        <Box display="flex" justifyContent="flex-end" mb={1}>
                            <Typography variant="caption" color="error">{editTotals.discountError}</Typography>
                        </Box>
                    )}
                    {editTotals.deliveryCharge > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2">Delivery Charge</Typography>
                            <Typography variant="body2">+{formatBd(editTotals.deliveryCharge)}</Typography>
                        </Box>
                    )}
                    {editTotals.vat > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2">VAT</Typography>
                            <Typography variant="body2">+{formatBd(editTotals.vat)}</Typography>
                        </Box>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={2}>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>New Total</Typography>
                        <Box textAlign="right">
                            {editOrder && Math.abs(editTotals.total - editOrder.total) > 0.0005 && (
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", textDecoration: "line-through" }}>
                                    {formatBd(editOrder.total)}
                                </Typography>
                            )}
                            <Typography variant="h4" color="primary" style={{ fontWeight: 800 }}>
                                {formatBd(editTotals.total)}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: "1rem" }}>
                    <Button onClick={closeEdit} color="default" disabled={editSaving}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={saveEdit}
                        disabled={editSaving || editItems.length === 0 || Boolean(editTotals.discountError)}
                        startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {editSaving ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Collection Payment Dialog: capture how a "Pay on Collection" order was actually settled */}
            <Dialog open={Boolean(collectPayOrder)} onClose={() => setCollectPayOrder(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" style={{ fontWeight: 700 }}>How did the customer pay?</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Order {displayOrderNo(collectPayOrder)} • {formatBd(collectPayOrder?.total)}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" style={{ gap: '12px', paddingTop: '8px', paddingBottom: '8px' }}>
                        {COLLECTION_PAYMENT_METHODS.map((method) => (
                            <Button
                                key={method}
                                variant="outlined"
                                color="primary"
                                size="large"
                                style={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, padding: '12px 16px' }}
                                onClick={() => updateOrderStatus(collectPayOrder.id, 3, method)}
                            >
                                {method}
                            </Button>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button onClick={() => setCollectPayOrder(null)} color="default">Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Customer Details Dialog */}
            <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>Customer Details</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedCustomer && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Mobile Number</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                                        {selectedCustomer.mobileNumber}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Email Address</Typography>
                                    <Typography variant="body1">
                                        {selectedCustomer.emailAddress || "-"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                                    <Typography variant="body1">
                                        {[
                                            selectedCustomer.building ? `Building ${selectedCustomer.building}` : null,
                                            selectedCustomer.road ? `Road ${selectedCustomer.road}` : null,
                                            selectedCustomer.block ? `Block ${selectedCustomer.block}` : null,
                                            selectedCustomer.districtArea,
                                            selectedCustomer.flatOfficeNo ? `Flat/Office ${selectedCustomer.flatOfficeNo}` : null
                                        ].filter(Boolean).join(", ")}
                                    </Typography>
                                    {selectedCustomer.shortAddress && (
                                        <Typography variant="caption" color="textSecondary">
                                            ({selectedCustomer.shortAddress})
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomerDialogOpen(false)} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Order Details Dialog */}
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>Order Details {displayOrderNo(selectedOrder)}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOrder && (() => {
                        const customer = getCustomer(selectedOrder);
                        return (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom color="primary">CUSTOMER INFO</Typography>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                    {customer?.firstName} {customer?.lastName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">{customer?.mobileNumber}</Typography>
                                <Typography variant="body2" style={{ marginBottom: '1.5rem' }}>
                                    {[customer?.building, customer?.road, customer?.block].filter(Boolean).join(", ")}
                                </Typography>

                                <Typography variant="subtitle2" gutterBottom color="primary">ORDER ITEMS</Typography>
                                {selectedOrder.walkInOrderItemsById?.map((item, idx) => (
                                    <Box key={idx} className={classes.orderItemRow}>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {item.serviceByServiceId?.nameEn || `Service ID: ${item.service_id || item.serviceId}`}
                                            </Typography>
                                            {item.serviceType?.nameEn && (
                                                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                    {item.serviceType.nameEn}
                                                </Typography>
                                            )}
                                            <Box className={classes.orderItemMeta}>
                                                <Box className={classes.orderItemBadge}>
                                                    Qty {item.quantity}
                                                </Box>
                                                <Box className={classes.orderItemPriceNote}>
                                                    {formatBd(item.price)} each
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                Line Total
                                            </Typography>
                                            <Typography variant="body2" style={{ fontWeight: 700 }}>
                                                {formatBd(item.total)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Subtotal</Typography>
                                    <Typography variant="body2">{formatBd(selectedOrder.subtotal)}</Typography>
                                </Box>
                                {selectedOrder.express && (
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="body2">Express</Typography>
                                        <Typography variant="body2">Yes</Typography>
                                    </Box>
                                )}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Discount</Typography>
                                    <Typography variant="body2" color="error">-{formatBd(selectedOrder.discount)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Delivery Charge</Typography>
                                    <Typography variant="body2">+{formatBd(selectedOrder.deliveryCharge)}</Typography>
                                </Box>
                                {selectedOrder.vat ? (
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="body2">VAT</Typography>
                                        <Typography variant="body2">+{formatBd(selectedOrder.vat)}</Typography>
                                    </Box>
                                ) : null}
                                <Box display="flex" justifyContent="space-between" mt={2}>
                                    <Typography variant="h5" style={{ fontWeight: 700 }}>Total</Typography>
                                    <Typography variant="h4" color="primary" style={{ fontWeight: 800 }}>{formatBd(selectedOrder.total)}</Typography>
                                </Box>
                                <Divider style={{ margin: '1.25rem 0' }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" color="primary">PAYMENT METHOD</Typography>
                                    <Chip
                                        label={selectedOrder.paymentMethod || "Not specified"}
                                        size="small"
                                        style={{
                                            backgroundColor: selectedOrder.paymentMethod ? "#eef0fb" : "#f1f3f9",
                                            color: selectedOrder.paymentMethod ? "#5e72e4" : "#8898aa",
                                            fontWeight: 700,
                                        }}
                                    />
                                </Box>
                                {/* Shown only once the order is settled. An unpaid order has no
                                    payment date to show, and a row reading "—" would be read as
                                    missing data rather than as money still owed. */}
                                {selectedOrder.paidOn && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
                                        <Typography variant="subtitle2" color="primary">PAID ON</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                                            {new Date(selectedOrder.paidOn).toLocaleDateString()}{" "}
                                            {new Date(selectedOrder.paidOn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                        </Typography>
                                    </Box>
                                )}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
                                    <Typography variant="subtitle2" color="primary">SERVED BY</Typography>
                                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                                        {selectedOrder.createdBy || "—"}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button onClick={() => setDetailsOpen(false)} color="default">Close</Button>
                    <Button startIcon={<Print />} onClick={() => reprintReceipt(selectedOrder)} color="primary">
                        Reprint Receipt
                    </Button>
                    {isAdmin && selectedOrder?.status !== 4 && (
                        <Button startIcon={<EditIcon />} onClick={() => handleOpenEdit(selectedOrder)} color="primary">
                            Edit Order
                        </Button>
                    )}
                    {selectedOrder?.status !== 3 && (
                        <Button variant="contained" color="primary" onClick={() => handleCompleteOrder(selectedOrder)}>
                            Mark as Completed
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default PosSales;
